import { ChangeEvent, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import type { DefaultOptionType } from 'antd/es/select';
import { yupResolver } from '@hookform/resolvers/yup';
import { cpf } from 'cpf-cnpj-validator';
import * as yup from 'yup';

import CustomerDto from '@/application/dto/CustomerDto';
import { InputFieldMask, InputField } from '@/components/Inputs';
import Button from '@/components/Button';

import { schema } from './customer.validation';
import { Container, InputContainer } from './style';
import InputAutocomplete from '@/components/Inputs/InputAutocomplete';

type FormData = yup.InferType<typeof schema>;

const domainList = [
  'yahoo.com',
  'hotmail.com',
  'gmail.com',
  'me.com',
  'aol.com',
  'mac.com',
  'live.com',
  'comcast.net',
  'googlemail.com',
  'msn.com',
  'hotmail.co.uk',
  'yahoo.co.uk',
  'facebook.com',
  'verizon.net',
  'sbcglobal.net',
  'att.net',
  'gmx.com',
  'outlook.com',
  'icloud.com',
];

export const CustomerForm = () => {
  const [options, setOptions] = useState<DefaultOptionType[]>([]);
  const { companiesSlug } = useParams();
  const { state, pathname } = useLocation();
  const navigate = useNavigate();
  const [documentMask, setDocumentMask] = useState('999.999.999-99999');

  useEffect(() => {
    const confirmEmail = document.getElementById('confirm-email');

    if (confirmEmail) {
      confirmEmail.onpaste = () => false;
    }
  }, []);

  const defaultValues = useMemo(() => {
    if (!state || !state.customer) {
      return {} as FormData;
    }
    return {
      name: state?.customer.name,
      email: state?.customer.email,
      document: state?.customer.document,
      confirmEmail: state?.customer.email,
    };
  }, [state]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
  } = useForm<FormData>({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const onSubmit = async ({ name, email, document }: FormData) => {
    try {
      const customer = new CustomerDto();
      customer.name = name;
      customer.email = email;
      customer.document = document;

      navigate(`/${companiesSlug}/checkout/payment`, {
        state: {
          ...state,
          customer,
          from: pathname,
        },
      });
    } catch (error) {}
  };

  const handleSearch = (value: string) => {
    setOptions(() => {
      if (!value) {
        return [];
      }

      const [username, domainValue] = value.split('@');

      if (domainValue) {
        return domainList
          .filter((domain) => domain.includes(domainValue))
          .map((domain) => ({
            label: `${username}@${domain}`,
            value: `${username}@${domain}`,
          }));
      }

      return [];
    });
  };

  return (
    <Container>
      <h1>Suas informações</h1>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="row">
          <InputContainer>
            <InputField
              {...register('name')}
              label="Nome*"
              placeholder="Digite seu nome completo"
              error={!!errors.name?.message}
              helperText={errors.name?.message}
            />
          </InputContainer>
        </div>
        <div className="row">
          <InputContainer>
            <InputAutocomplete
              placeholder="Digite seu email"
              label="Email*"
              style={{ width: 200 }}
              onSearch={handleSearch}
              defaultValue={getValues().email}
              onChange={(value) => setValue('email', value)}
              onSelect={() => setOptions([])}
              options={options}
              allowClear={true}
              error={!!errors.email?.message}
              helperText={errors.email?.message}
            />
          </InputContainer>
        </div>
        <div className="row">
          <InputContainer>
            <InputAutocomplete
              label="Confirmação de email*"
              style={{ width: 200 }}
              defaultValue={getValues().email}
              onSearch={handleSearch}
              onChange={(value) => setValue('confirmEmail', value)}
              placeholder="Digite seu email novamente"
              options={options}
              onSelect={() => setOptions([])}
              allowClear={true}
              error={!!errors.confirmEmail?.message}
              helperText={errors.confirmEmail?.message}
              id="confirm-email"
            />
          </InputContainer>
        </div>
        <div className="row">
          <InputContainer>
            <InputFieldMask
              {...register('document')}
              label="Documento*"
              mask={documentMask}
              maskPlaceholder={null}
              placeholder="CPF/CNPJ"
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                const { value } = e.target;

                if (cpf.strip(value).length < 12) {
                  setDocumentMask('999.999.999-999');
                } else {
                  setDocumentMask('99.999.999/9999-99');
                }
              }}
              error={!!errors.document?.message}
              helperText={errors.document?.message}
            />
          </InputContainer>
        </div>
        <div className="row">
          <Button text="Ir para pagamento" />
        </div>
      </form>
    </Container>
  );
};
